import { t } from "i18next";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { ButtonGroup } from "../../core/components/buttons/ButtonGroup";
import { ProgressBar } from "../../core/components/progress/ProgressBar";
import {
  IPAD_MINI_BREAKPOINT,
  IPAD_MINI_BREAKPOINT_NUMBER,
} from "../../core/styles/breakpoints";
import { useMainStore } from "../../core/store/mainStore";
import { useStyles } from "../../core/styles/theme";
import useWindowDimensions from "../../core/utils/useWindowDimensions";
import { useWidgetConfig } from "../../widget/WidgetContext";
import {
  getAppointmentFlowScreens,
  getStepProgress,
  usePracticeSelectionSkip,
} from "../appointment/utils/appointmentFlow";
import { useHeaderBackContext } from "../navigation/context/useHeaderContext";
import {
  AppointmentBookingStepNames,
  useHandleAppointmentBookingNavigation,
} from "../navigation/utils/useHandleAppointmentBookingNavigation";

type Props = {
  children: React.ReactNode;
  hideNextButton?: boolean;
  nextButtonText?: string;
  onNext?: () => void;
  nextButtonDisabled?: boolean;
  nextButtonLoading?: boolean;
  handleBack?: () => void;
  hideBackButton?: boolean;
};

export const AppointmentScreenWrapper = (props: Props) => {
  const theme = useStyles();
  const location = useLocation();
  const navigate = useNavigate();

  const currentScreen = location.pathname
    .split("/")
    .pop() as AppointmentBookingStepNames;

  // PF-5480: the step list is derived from the flow this chain actually walks through, so a skipped practice
  // selection or a hidden preferences step shifts the bar instead of leaving a gap. Subscribed through the
  // store because chainConfig arrives asynchronously.
  const { isSkipped } = usePracticeSelectionSkip();
  const flowScreens = useMainStore((state) =>
    getAppointmentFlowScreens(state, isSkipped),
  );
  const progress = getStepProgress(flowScreens, currentScreen);

  useHeaderBackContext({
    handleBack: props.handleBack,
    hideBackButton: props.hideBackButton,
  });

  const navigateToNextStep = useHandleAppointmentBookingNavigation();
  const handleNext = () => {
    if (props.onNext) {
      props.onNext();
    } else {
      navigateToNextStep();
    }
  };

  const handleGoBack = () => {
    if (props.handleBack) {
      props.handleBack();
    } else {
      navigate(-1);
    }
  };

  // useHeaderBackContext({ handleBack, hideBackButton: props.hideBackButton });

  const { isWidget } = useWidgetConfig();
  const width = useWindowDimensions().width;
  const isDesktop = !isWidget && width >= IPAD_MINI_BREAKPOINT_NUMBER;

  return (
    <ContentContainer $isWidget={isWidget}>
      {!isDesktop && progress && (
        <ProgressBar
          completedSteps={progress.step}
          totalSteps={progress.totalSteps}
        />
      )}
      <ChildrenContainer $isWidget={isWidget}>
        {props.children}
        <StyledButtonGroup
          spaceBetween={isWidget}
          progressButton={
            props.hideNextButton
              ? undefined
              : {
                  onClick: handleNext,
                  disabled: props.nextButtonDisabled,
                  loading: props.nextButtonLoading,
                  text: props.nextButtonText ?? t("NEXT"),
                  iconName: "ArrowRight",
                  primaryColor: theme.colors.dark,
                  noMinWidth: isWidget,
                }
          }
          textButton={
            isWidget && !props.hideBackButton
              ? {
                  text: t("BACK"),
                  onClick: handleGoBack,
                  color: theme.colors.dark,
                  icon: {
                    name: "ArrowLeftSmall",
                    size: 24,
                    color: theme.colors.dark,
                  },
                  noPadding: true,
                }
              : undefined
          }
        />
      </ChildrenContainer>
    </ContentContainer>
  );
};

const ContentContainer = styled.div<{ $isWidget: boolean }>`
  display: flex;
  flex-direction: column;
  max-width: 640px;
  width: 100%;
  align-items: center;
  justify-content: flex-start;
  height: 100%;
  padding-top: ${({ $isWidget }) => ($isWidget ? "0px" : "20px")};
  gap: 20px;
`;

const StyledButtonGroup = styled(ButtonGroup)`
  align-items: flex-end;
  width: 100%;
`;

const ChildrenContainer = styled.div<{ $isWidget: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${({ $isWidget }) => ($isWidget ? "20px" : "40px")};

  @media (max-width: ${IPAD_MINI_BREAKPOINT}) {
    padding: 20px;
    border-radius: 15px;
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
    background-color: ${({ theme }) => theme.colors.background};
  }
  ${({ $isWidget, theme }) =>
    $isWidget &&
    `
    padding: 20px;
    border-radius: 15px;
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
    background-color: ${theme.colors.background};
  `}
`;
